import * as YAML from 'yaml';

// Enhanced error type for better error reporting
export interface YamlError {
  message: string;
  line?: number;
  column?: number;
  position?: number;
}

// Parse YAML with proper error reporting
export const parseYaml = (yamlStr: string): { success: true; data: any } | { success: false; error: YamlError } => {
  if (!yamlStr.trim()) {
    return { success: true, data: {} };
  }

  try {
    // First try to parse as YAML
    const doc = YAML.parseDocument(yamlStr);

    if (doc.errors.length > 0) {
      const error = doc.errors[0];
      return {
        success: false,
        error: {
          message: error.message,
          line: error.linePos?.[0]?.line,
          column: error.linePos?.[0]?.col,
          position: error.pos?.[0]
        }
      };
    }

    return {
      success: true,
      data: doc.toJS()
    };
  } catch (error) {
    // Extract position info from JSON errors if available
    const message = error instanceof Error ? error.message : 'Invalid YAML syntax';
    const positionMatch = message.match(/position (\d+)/i);
    const lineMatch = message.match(/line (\d+)/i);

    let position: number | undefined;
    let line: number | undefined;
    let column: number | undefined;

    if (positionMatch) {
      position = parseInt(positionMatch[1]);
      const lines = yamlStr.substring(0, position).split('\n');
      line = lines.length;
      column = lines[lines.length - 1].length + 1;
    } else if (lineMatch) {
      line = parseInt(lineMatch[1]);
    }

    return {
      success: false,
      error: {
        message,
        line,
        column,
        position
      }
    };
  }
};

// Validate YAML content and return detailed error info
export const validateYamlContent = (content: string): YamlError | null => {
  if (!content.trim()) {
    return null; // Empty content is valid
  }

  const result = parseYaml(content);

  if (!result.success) {
    return result.error;
  }

  // Validate against SemanticModel interface
  const validationError = validateSemanticModelStructure(result.data);
  if (validationError) {
    return { message: validationError };
  }

  return null; // No errors
};


// Helper function to update the semantic model state and keep YAML in sync
// Comprehensive validation against SemanticModel interface
const validateSemanticModelStructure = (data: any): string | null => {
  if (!data || typeof data !== 'object') {
    return 'Configuration must be an object';
  }

  // Validate top-level properties
  if (data.name !== undefined && typeof data.name !== 'string') {
    return 'Field "name" must be a string';
  }

  if (data.description !== undefined && typeof data.description !== 'string') {
    return 'Field "description" must be a string';
  }

  if (data.custom_instructions !== undefined && typeof data.custom_instructions !== 'string') {
    return 'Field "custom_instructions" must be a string';
  }

  if (data.module_custom_instructions !== undefined && typeof data.module_custom_instructions !== 'object') {
    return 'Field "module_custom_instructions" must be an object';
  }

  // Validate tables array
  if (data.tables !== undefined) {
    if (!Array.isArray(data.tables)) {
      return 'Field "tables" must be an array';
    }

    for (let i = 0; i < data.tables.length; i++) {
      const table = data.tables[i];
      if (!table || typeof table !== 'object') {
        return `Table at index ${i} must be an object`;
      }

      if (!table.name || typeof table.name !== 'string') {
        return `Table at index ${i} must have a "name" field (string)`;
      }

      if (table.base_table) {
        if (typeof table.base_table !== 'object') {
          return `Table "${table.name}" base_table must be an object`;
        }
        if (typeof table.base_table.database !== 'string') {
          return `Table "${table.name}" base_table.database must be a string`;
        }
        if (typeof table.base_table.schema !== 'string') {
          return `Table "${table.name}" base_table.schema must be a string`;
        }
        if (typeof table.base_table.table !== 'string') {
          return `Table "${table.name}" base_table.table must be a string`;
        }
      }

      // Validate array fields in table
      const arrayFields = ['dimensions', 'time_dimensions', 'facts', 'metrics', 'filters', 'synonyms'];
      for (const field of arrayFields) {
        if (table[field] !== undefined && !Array.isArray(table[field])) {
          return `Table "${table.name}" field "${field}" must be an array`;
        }
      }
    }
  }

  // Validate relationships array
  if (data.relationships !== undefined) {
    if (!Array.isArray(data.relationships)) {
      return 'Field "relationships" must be an array';
    }

    for (let i = 0; i < data.relationships.length; i++) {
      const rel = data.relationships[i];
      if (!rel || typeof rel !== 'object') {
        return `Relationship at index ${i} must be an object`;
      }

      if (!rel.name || typeof rel.name !== 'string') {
        return `Relationship at index ${i} must have a "name" field (string)`;
      }

      if (typeof rel.left_table !== 'string') {
        return `Relationship "${rel.name}" left_table must be a string`;
      }

      if (typeof rel.right_table !== 'string') {
        return `Relationship "${rel.name}" right_table must be a string`;
      }

      if (rel.relationship_columns && !Array.isArray(rel.relationship_columns)) {
        return `Relationship "${rel.name}" relationship_columns must be an array`;
      }
    }
  }

  return null; // No errors
};

// Validation function for table name
export const validateTableName = (name: string): string | null => {
  if (!name.trim()) {
    return 'Table name is required';
  }

  // Check for valid characters: a-z, 0-9, underscore only
  const validNameRegex = /^[a-zA-Z0-9_]+$/;
  if (!validNameRegex.test(name)) {
    return 'Table name can only contain lowercase letters (a-z), numbers (0-9), and underscores (_)';
  }

  // Check for leading/trailing underscores
  if (name.startsWith('_') || name.endsWith('_')) {
    return 'Table name cannot start or end with an underscore';
  }

  // Check for consecutive underscores
  if (name.includes('__')) {
    return 'Table name cannot contain consecutive underscores';
  }

  return null;
};
